import { formatDistanceToNow } from 'date-fns'
import { useAdminStatus } from '@/hooks/useApi'
import { useTestbedEnvironment } from '@/hooks/useTestbedEnvironment'
import { isTestbed } from '@/lib/testbed'

// Format relative time (e.g., "3 min ago")
function formatRelativeTime(date?: Date | string | null) {
  if (!date) return null
  try {
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) return null
    return formatDistanceToNow(dateObj, { addSuffix: true })
  } catch {
    return null
  }
}

// Format full timestamp for tooltip
function formatFullTimestamp(date?: Date | string | null) {
  if (!date) return null
  try {
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) return null
    return dateObj.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    })
  } catch {
    return null
  }
}

// Get GitHub commit URL
function getCommitUrl(commitSha?: string | null, repo = 'admin-ui') {
  if (!commitSha || commitSha === 'dev' || commitSha === 'unknown') return null
  return `https://github.com/natalan/${repo}/commit/${commitSha}`
}

interface BuildInfoRowProps {
  label: string
  commitSha?: string | null
  buildTime?: Date | string | null
  repo?: string
  showUnknown?: boolean
}

function BuildInfoRow({
  label,
  commitSha,
  buildTime,
  repo,
  showUnknown = false,
}: BuildInfoRowProps) {
  const relativeTime = formatRelativeTime(buildTime)
  const fullTimestamp = formatFullTimestamp(buildTime)
  const commitUrl = getCommitUrl(commitSha, repo)
  const shortSha =
    commitSha && commitSha !== 'dev' && commitSha !== 'unknown' ? commitSha.substring(0, 7) : null

  // Show row if we have either commit SHA or build time, or if showUnknown is true
  if (!shortSha && !buildTime && !showUnknown) {
    return null
  }

  return (
    <div className="text-[10px] text-textMuted">
      <span className="font-medium">{label}:</span>{' '}
      {shortSha ? (
        commitUrl ? (
          <a
            href={commitUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-textMuted hover:text-white/80 underline"
          >
            {shortSha}
          </a>
        ) : (
          <span className="font-mono">{shortSha}</span>
        )
      ) : (
        <span className="font-mono">unknown</span>
      )}
      {relativeTime && (
        <>
          {', '}
          <span className="cursor-help" title={fullTimestamp || undefined}>
            {relativeTime}
          </span>
        </>
      )}
    </div>
  )
}

interface BuildInfoProps {
  collapsed?: boolean
}

/**
 * Footer marker of a testbed session (CAP-951): there is no build to describe locally, so it says
 * which environment this is and how old its copy of production is.
 */
function TestbedInfo({ collapsed }: BuildInfoProps) {
  const { data: environment, isError } = useTestbedEnvironment()
  const name = environment?.name ?? null
  const createdAt = environment?.snapshot?.created_at
  const dataAge = formatRelativeTime(createdAt)
  const title = [
    name ? `Environment ${name}` : null,
    createdAt ? `Data copied ${formatFullTimestamp(createdAt)}` : null,
  ]
    .filter(Boolean)
    .join('. ')

  if (collapsed) {
    return (
      <div className="px-2 py-3 border-t border-border" title={title || undefined}>
        <div className="flex flex-col items-center gap-1">
          <span className="rounded bg-yellow-500/20 px-1 text-[10px] font-semibold uppercase tracking-wide text-yellow-300">
            Testbed
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="px-3 py-3 border-t border-border" title={title || undefined}>
      <div className="flex flex-col gap-1.5 text-xs text-textMuted">
        <div className="flex items-center gap-2 text-[10px]">
          <span className="rounded bg-yellow-500/20 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-yellow-300">
            Testbed
          </span>
          {name && <span className="font-mono text-textMuted">{name}</span>}
        </div>
        <div className="text-[10px] text-textMuted">
          {dataAge ? (
            <>
              <span className="font-medium">Data:</span> copied {dataAge}
            </>
          ) : isError ? (
            'Environment details unavailable'
          ) : (
            'Environment details loading'
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * BuildInfo component - displays build version and deployment time
 */
export function BuildInfo({ collapsed }: BuildInfoProps) {
  if (isTestbed()) {
    return <TestbedInfo collapsed={collapsed} />
  }
  return <ProductionBuildInfo collapsed={collapsed} />
}

function ProductionBuildInfo({ collapsed }: BuildInfoProps) {
  // Get UI build info from environment variables (injected at build time)
  const uiCommitSha = import.meta.env.VITE_BUILD_COMMIT || 'dev'
  const uiBuildTime = import.meta.env.VITE_BUILD_TIME
    ? new Date(import.meta.env.VITE_BUILD_TIME)
    : null

  // API build info comes from /admin/status (`useAdminStatus`, one shared
  // query key, CAP-744). A failure or a response without build info just shows "unknown"; errors
  // are not surfaced to users in the sidebar.
  const { data: status } = useAdminStatus()
  const apiBuildInfo = status?.build ?? null

  // If collapsed, show minimal info
  if (collapsed) {
    return (
      <div className="px-2 py-3 border-t border-border">
        <div className="flex flex-col items-center gap-1">
          <div className="text-[10px] text-textMuted font-mono">{uiCommitSha}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-3 py-3 border-t border-border">
      <div className="flex flex-col gap-1.5 text-xs text-textMuted">
        <BuildInfoRow label="UI" commitSha={uiCommitSha} buildTime={uiBuildTime} repo="admin-ui" />
        <BuildInfoRow
          label="API"
          commitSha={apiBuildInfo?.commitSha}
          buildTime={apiBuildInfo?.buildTime ? new Date(apiBuildInfo.buildTime) : null}
          repo="admin-api"
          showUnknown={apiBuildInfo === null}
        />
      </div>
    </div>
  )
}
